import React from 'react';
import { Box, Tooltip } from '@mui/material';
import type { Service } from '../../config/infra';
import type { ServiceState } from '../../api/statusApi';

// Colour per resolved state. Timers/host-services don't participate in up/down
// (the docker-status endpoint doesn't report them), so they get a neutral dot.
const DOT: Record<ServiceState | 'neutral', string> = {
    up: '#4caf50',       // green
    down: '#e53935',     // red
    unknown: '#9e9e9e',  // grey
    neutral: '#5c6bc0',  // indigo — "not a container, informational"
};

interface Props {
    service: Service;
    state: ServiceState;
    /** runtime detail for the tooltip, e.g. "Up 3 hours" */
    detail?: string;
    /** Opens this service's detail page (HOME-188) — every chip is clickable;
     *  the detail view itself is metrics/info-only for services with no
     *  `actions` configured. */
    onClick?: () => void;
}

const ServiceChip: React.FC<Props> = ({ service, state, detail, onClick }) => {
    const isContainer = service.kind === 'container';
    const dot = isContainer ? DOT[state] : DOT.neutral;
    const label = service.label ?? service.name;
    const hasActions = (service.actions?.length ?? 0) > 0;

    const kindWord = service.kind === 'container' ? 'container'
        : service.kind === 'timer' ? 'scheduled job' : 'host service';
    const stateWord = !isContainer ? '' : ` — ${state}${detail ? ` (${detail})` : ''}`;
    const actionsWord = hasActions ? ' · has actions' : '';
    const tip = `${label} · ${kindWord}${stateWord}${actionsWord}${service.note ? `\n${service.note}` : ''}`;

    return (
        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tip}</span>} arrow>
            <Box
                component={onClick ? 'button' : 'div'}
                type={onClick ? 'button' : undefined}
                onClick={onClick}
                data-testid={`service-${service.name}`}
                data-state={isContainer ? state : 'neutral'}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1,
                    py: 0.5,
                    borderRadius: 9999,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    fontFamily: 'inherit',
                    backgroundColor: 'action.hover',
                    border: 1,
                    borderColor: hasActions ? 'primary.main' : 'divider',
                    fontStyle: isContainer ? 'normal' : 'italic',
                    opacity: isContainer ? 1 : 0.8,
                    cursor: onClick ? 'pointer' : 'default',
                    '&:hover': onClick ? { backgroundColor: 'action.selected' } : undefined,
                }}
            >
                <Box
                    component='span'
                    sx={{
                        width: 8, height: 8, borderRadius: '50%',
                        backgroundColor: dot, flexShrink: 0,
                        boxShadow: isContainer && state === 'up' ? `0 0 4px ${dot}` : 'none',
                    }}
                />
                {label}
            </Box>
        </Tooltip>
    );
};

export default ServiceChip;
